const asNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

const sourceConfig = {
  lead: {
    origin: "Lead",
    sourceIdField: "leadId",
    name: (source) => source.nome || source.name || "",
    phone: (source) => source.telefone || source.phone || "",
    value: (source) => source.valor_venda,
    saleDate: (source) => source.data_fechamento || source.fechou_venda_em || "",
    product: (source) => source.produto_venda || source.produto_anuncio || source.produto || "",
  },
  prospeccao: {
    origin: "Prospecção",
    sourceIdField: "prospId",
    name: (source) => source.name || source.nome || "",
    phone: (source) => source.phone || source.telefone || "",
    value: (source) => source.value,
    saleDate: (source) => source.saleDate || source.data_fechamento || source.date || source.createdAt || "",
    product: (source) => source.product || source.produto || "",
  },
};

export function financialSaleSheetId(sourceType, sourceId) {
  const safeSourceId = String(sourceId || "unknown").replace(/[.#$[\]/]/g, "_");
  return `financial_${sourceType}_${safeSourceId}`;
}

export function isClosedSaleSource(sourceType, source) {
  if (!source) return false;
  return sourceType === "lead"
    ? source.estagio === "fechou_venda" && asNumber(source.valor_venda) > 0
    : source.stage === "venda" && asNumber(source.value) > 0;
}

export function findFinancialSaleSheet(clients, sourceType, sourceId, source = null) {
  const config = sourceConfig[sourceType];
  if (!config) return null;
  const canonical = value => sourceType === "lead" ? String(value || "").replace(/^aurora_/, "") : String(value);
  const id = canonical(sourceId);
  const directlyLinked = (clients || []).find((client) => (
    client?.financialSourceType === sourceType
    && canonical(client.financialSourceId) === id
    && (!source?.storeId || Number(client.storeId) === Number(source.storeId))
  )) || (clients || []).find((client) => canonical(client?.[config.sourceIdField]) === id
    && (!source?.storeId || Number(client.storeId) === Number(source.storeId))) || null;
  // Phone is not a sale identifier. A contact can make more than one purchase,
  // including through different channels, so an unlinked client card must never
  // be converted into a source-linked financial sheet just because its phone
  // matches the new sale. Explicit source IDs above are the only safe reuse key.
  return directlyLinked;
}

export function buildFinancialSaleSheet({ sourceType, source, saleData = {}, today }) {
  const config = sourceConfig[sourceType];
  if (!config || !source) throw new Error("Origem financeira inválida.");

  const sourceId = String(source.id || source._rawPhone || "");
  if (!sourceId) throw new Error("Venda sem identificador de origem.");

  const value = asNumber(saleData.valor ?? config.value(source));
  const saleDate = saleData.data || config.saleDate(source) || today;
  const product = saleData.produtos ?? config.product(source);
  const paymentMethod = saleData.formaPagamento ?? source.forma_pagamento ?? source.paymentMethod ?? "";
  const saleNotes = saleData.observacoes ?? source.observacoes_venda ?? source.saleNotes ?? "";

  return {
    id: financialSaleSheetId(sourceType, sourceId),
    financeOnly: true,
    financialSourceType: sourceType,
    financialSourceId: sourceId,
    financialSourcePath: `${sourceType === "lead" ? "leads" : "arflow_prospeccao"}/${sourceId.replace(/^aurora_/, "")}`,
    ...(source.saleConfirmedAt ? {saleConfirmedAt:source.saleConfirmedAt} : {}),
    [config.sourceIdField]: sourceId,
    origin: config.origin,
    name: config.name(source),
    phone: config.phone(source),
    consultantId: saleData.consultantId ?? source.consultantId ?? null,
    storeId: saleData.storeId ?? source.storeId ?? null,
    stage: "closed",
    value,
    paidValue: saleData.paidValue ?? source.paidValue ?? value,
    product,
    data_fechamento: saleDate,
    stageDates: { ...(source.stageDates || {}), closed: saleDate },
    createdAt: source.createdAt || source.data_criacao || source.date || saleDate,
    paymentMethod,
    saleNotes,
    deliveryDate: saleData.deliveryDate ?? source.deliveryDate ?? null,
    followUp: saleData.followUp ?? source.followUp ?? null,
    posVendaDate: saleData.posVendaDate ?? source.posVendaDate ?? null,
    nextSaleDate: saleData.nextSaleDate ?? source.nextSaleDate ?? null,
    nextSaleValue: saleData.nextSaleDate ? value : (source.nextSaleValue ?? null),
    nextSaleNote: saleData.nextSaleNote ?? source.nextSaleNote ?? null,
  };
}

export function mergeFinancialSaleSheet(existing, candidate, { preserveExisting = false } = {}) {
  if (!existing) return candidate;
  const sourceIdentity=Object.fromEntries(["financialSourceType","financialSourceId","financialSourcePath","leadId","prospId"]
    .filter(field=>existing[field]!=null&&existing[field]!=="").map(field=>[field,existing[field]]));
  if (!preserveExisting) return {
    ...existing,
    ...candidate,
    ...sourceIdentity,
    id: existing.id,
    stageDates: { ...(existing.stageDates || {}), ...(candidate.stageDates || {}) },
  };

  const preferExisting = (field) => (
    existing[field] !== undefined && existing[field] !== null && existing[field] !== ""
      ? existing[field]
      : candidate[field]
  );
  return {
    ...existing,
    ...candidate,
    ...sourceIdentity,
    id: existing.id,
    value: preferExisting("value"),
    paidValue: preferExisting("paidValue"),
    consultantId: preferExisting("consultantId"),
    storeId: preferExisting("storeId"),
    data_fechamento: preferExisting("data_fechamento"),
    product: preferExisting("product"),
    paymentMethod: preferExisting("paymentMethod"),
    saleNotes: preferExisting("saleNotes"),
    deliveryDate: preferExisting("deliveryDate"),
    followUp: preferExisting("followUp"),
    posVendaDate: preferExisting("posVendaDate"),
    nextSaleDate: preferExisting("nextSaleDate"),
    nextSaleValue: preferExisting("nextSaleValue"),
    nextSaleNote: preferExisting("nextSaleNote"),
    stageDates: { ...(candidate.stageDates || {}), ...(existing.stageDates || {}) },
  };
}